<template>
    <el-popover
        v-model:visible="vis"
        trigger="click"
        :popper-style="{ width: `${width}px`, padding: 0 }"
        :hide-after="0"
    >

        <!-- input -->
        <template #reference>
            <el-input
                ref="input"
                v-model="val"
                v-bind="$attrs"
                @input="refresh()"
                @keyup.enter="onSearch()"
            >
                <template #suffix>
                    <CircleClose
                        v-if="val"
                        p4
                        w16
                        h16
                        cursor-pointer
                        @click.stop="onClear"
                    />
                    <Search
                        p4
                        ml4
                        w16
                        h16
                        cursor-pointer
                        @click.stop="onSearch"
                    />
                </template>
            </el-input>
        </template>

        <!-- header -->
        <!-- 一级分类 -->
        <div
            flex="~ wrap"
            p12
            mb--12
            text-14
            @click="input.focus()"
        >
            <div
                v-for="opt in keysed"
                :key="opt.key"
                :class="['btn', Object.is(currentKey, opt.key) && 'is-active']"
                mr8
                mb8
                lh-22
                text-black
                @click="toggle(opt)"
            >{{ opt.name }}</div>
        </div>
        <!-- 下级分类 -->
        <div
            flex="~ wrap"
            p12
            mb--12
            text-14
            @click="input.focus()"
        >
          <div
                v-for="opt in nextKeysed(keysed)"
                :key="opt.key"
                :class="['btn', keys.includes(opt.key) && 'is-active']"
                mr8
                mb8
                lh-22
                text-black
                @click="toggle(opt)"
            >{{ opt.value }}</div>
        </div>

        <div b-b="1 solid #00000015" />

        <!-- options -->
        <div
            v-if="options?.length && !loading"
            max-h500
            overflow-auto
            py8
        >
            <div
                v-for="(opt,index) in options"
                :key="index"
                class="item"
                truncate
                @click="onSelect(opt)"
                v-html="highlight(typeof label == 'function' ? label(opt, keys) : opt[label])"
            />
        </div>

        <div
            v-if="!options?.length && !loading"
            py14
            text="#00000080"
            text-center
        >
            无数据
        </div>

        <!-- 加载中 -->
        <div
            v-if="loading"
            py11
            text="#00000080"
            text-center
            lh="1em"
        >
            <el-icon
                class="is-loading"
                text-22
            ><Loading /></el-icon>
        </div>
    </el-popover>
</template>

<script setup>
import { nextTick, ref, watch, computed } from 'vue';
import { useElementSize, useVModel } from '@vueuse/core';
import { remove } from '@vue/shared';
import { useRequest } from 'vue-request';
import { deepClone } from '@/utils';

const props = defineProps({
    modelValue: String,
    initKeys: Array,
    fetchKeys: Function,
    fetchOptions: Function,
    label: [String, Function],
    single: Boolean
});

const emit = defineEmits(['update:modelValue', 'search', 'clear', 'update:keys', 'select']);

const vis = ref(false);

const val = useVModel(props, 'modelValue', undefined, { passive: true });
const keys = ref([...(props.initKeys || [])]);
const currentKey = ref(props.initKeys[0] || '');

watch(val, (val) => val && (vis.value = true));

const { data: keysed } = useRequest(async() => (await props.fetchKeys()).map(nOpt));

const { data: options, refresh, loading } = useRequest(async(kw) => (await props.fetchOptions(val.value, getRequiredKeys(keys.value))).map(nOpt), { manual: true, throttleInterval: 600 });

const nextKeysed = computed(() => (data) => {
  return currentKey.value === 'all' ? [] : (data?.filter(group => group.key === currentKey.value)[0]?.children || []);
});

// 判断二级是否有选中项
function checkHalfSelected(arr = []) {
  let count = 0;
  const len = arr.length;
  arr.forEach(n => {
    count += Number(!!n.select);
  });
  return count > 0 && count < len;
}

function fmtKeyData(keys = [], sourceArr = []) {
  const tempArr = deepClone(sourceArr);
  tempArr.map(p => {
    p['select'] = keys.includes(p.key);
    if (p.children) {
      p.children = p.children.map(child => {
        child.select = keys.includes(child.key);
        return child;
      });
    }
    return p;
  });
  return tempArr;
}

// 处理后端需要的key，排除all，如果只选择一级，需要传一级下所有的二级并排除一级，如果选择完一级后又选择二级则只需要选择二级并排除一级
function getRequiredKeys(keys = []) {
  const fmtData = fmtKeyData(keys, keysed.value);
  const recuHandler = (arr = [], result = []) => {
    arr.forEach(item => {
      const { key, select, children } = item;
      if (select && key !== 'all') {
        if (children) {
          const isHalfSelected = checkHalfSelected(children);
          // 说明只选择了一级，则把二级全部赋为选中
          if (!isHalfSelected) {
            children.map(ch => {
              ch.select = true;
              return ch;
            })
          }
        } else {
          // 只插入最后一级的key
          result.push(key);
        }
        children && recuHandler(children, result);
      }
    });
    return result;
  }
  return recuHandler(fmtData, []);
}

function toggle(opt) {
    const hasChildren = Object.prototype.hasOwnProperty.call(opt, 'children');
    hasChildren && (currentKey.value = opt.key);
    // 先清除一级分类的key与之前选中下级的key
    hasChildren && keys.value.forEach(k => {
      const target = keysed.value.find(n => n.key === k);
      const isRequireClear = target && Object.prototype.hasOwnProperty.call(target, 'children');
      if (isRequireClear) {
        remove(keys.value, k);
        target.children.forEach(next => {
          remove(keys.value, next.key);
        });
      }
    });
    // 清除结束
    if (keys.value.includes(opt.key)) {
        remove(keys.value, opt.key);
        if (hasChildren) {
          // 一级分类
          opt.children.forEach(item => {
            remove(keys.value, item.key);
          });
        }
    } else {
        keys.value.push(opt.key);
    }
    refresh();
}

const input = ref();
const { width } = useElementSize(input);

async function onSelect(row) {
    emit('update:keys', getRequiredKeys(keys.value));
    const label = typeof props.label === 'function' ? props.label(row, keys.value) : row[props.label];
    emit('update:modelValue', label);
    emit('select', row);
    setTimeout(() => {
        vis.value = false;
    }, 0);
}

async function onClear() {
    emit('update:modelValue', '');
    await nextTick();
    emit('clear');
}

async function onSearch() {
    emit('update:keys', getRequiredKeys(keys.value));
    await nextTick();
    emit('search');
    vis.value = false;
}

function highlight(txt) {
    const kw = props.modelValue;
    if (!kw) return txt;
    if (!txt) return txt;
    return txt.replace(new RegExp(kw, 'ig'), `<span style="color: var(--el-color-primary)">${kw}</span>`);
}

function nOpt(opt) {
    return (
        Array.isArray(opt) ? { label: opt[0], value: opt[1] }
            : typeof opt === 'object' ? opt
                : { label: opt, value: opt }
    );
}
</script>

<style scoped lang="scss">
.btn {
  @apply px-12 rd-full b-(1 solid #00000015) hover:bg-#00000010 cursor-pointer;

  &.is-active {
    @apply  c-#179aee b-#179aee;
  }
}

.item {
  color: var(--el-text-color-regular);
  height: 34px;
  line-height: 34px;
  padding: 0 32px 0 20px;
  cursor: pointer;

  &:hover {
    background: var(--el-fill-color-light);
  }

  &.is-active {
    color: var(--el-color-primary);
  }
}
</style>
